// Loading skeleton for the original (classic) rates form: index.html paints #form-boot (header with logo, grey tile blocks,
// footer) before main.js has loaded, only on /communication/rates-form without startAssistant=1, and sets
// window.__formBootDone. The rates form calls it once its first step is on screen and styled, so the skeleton comes down
// in one change. Hope Direct has its own card (#hope-boot, window.__hopeBootDone) and never gets this one.

/** How long after the view is ready the rates form waits for its first step before lifting the skeleton anyway. */
export const FORM_BOOT_SETTLE_CAP_MS = 3000;

/**
 * The site's main stylesheet loads asynchronously (index.html: media="print" switched to all on load). A step shown before
 * it applies is unstyled, so the skeleton waits for it. True once a main stylesheet is applied with other than print media,
 * or when the page links none (tests, a build that inlines it).
 */
export function formStylesApplied(doc: Document): boolean {
  try {
    const main = /\/styles(?:-[A-Za-z0-9]+)?\.css(?:[?#]|$)/;
    const linked = Array.from(doc.querySelectorAll('link[rel="stylesheet"]')).some((l: any) => main.test(l.href || ''));
    if (!linked) { return true; }
    return Array.from(doc.styleSheets).some((sh: any) => main.test(sh.href || '') && !(sh.media && sh.media.mediaText === 'print'));
  } catch (e) { return true; }
}

/** True when a step card is laid out and every image shown in it has loaded. */
export function formFirstStepReady(root: ParentNode | null): boolean {
  try {
    if (!root) { return false; }
    const step = Array.from(root.querySelectorAll('app-step')).find((s: any) => s.getBoundingClientRect().height > 20) as any;
    if (!step) { return false; }
    return !Array.from(step.querySelectorAll('img')).some((i: any) =>
      i.getBoundingClientRect().height > 0 && !(i.complete && i.naturalWidth > 0));
  } catch (e) { return true; }
}

/** Lift the skeleton once. False when there is none (Hope Direct, another page, already lifted). */
export function dismissFormBoot(win: any): boolean {
  try {
    const done = win && win.__formBootDone;
    if (typeof done !== 'function') { return false; }
    win.__formBootDone = null;
    done();
    return true;
  } catch (e) {
    try { win.__formBootDone = null; } catch (x) { /* non-fatal */ }
    return false;
  }
}

/**
 * Lift the skeleton the frame the first step is ready (laid out, images in, main stylesheet applied), or after
 * FORM_BOOT_SETTLE_CAP_MS whatever the state, so a slow image or stylesheet never holds the form. Stops when stopped() is true.
 */
export function watchFormBoot(win: any, doc: Document, root: ParentNode | null, stopped: () => boolean,
                              capMs: number = FORM_BOOT_SETTLE_CAP_MS): void {
  try {
    if (!win || typeof win.__formBootDone !== 'function') { return; }
    const until = Date.now() + capMs;
    const raf = typeof win.requestAnimationFrame === 'function'
      ? (fn: () => void) => win.requestAnimationFrame(fn)
      : (fn: () => void) => setTimeout(fn, 16);
    const check = () => {
      try {
        if (stopped() || typeof win.__formBootDone !== 'function') { return; }
        if ((formFirstStepReady(root) && formStylesApplied(doc)) || Date.now() > until) {
          dismissFormBoot(win);
          return;
        }
        raf(check);
      } catch (e) {
        dismissFormBoot(win);
      }
    };
    raf(check);
  } catch (e) {
    dismissFormBoot(win);
  }
}
